// 리드 처리 상태 — FE 단일 소스 (라벨/색/드롭다운 옵션).
// DB enum(packages/db leads.ts) 과 값·라벨 동기화. 순수 데이터 (React 의존 없음).
//
// 활성 14 = 대기 + 13종. 레거시 3(in_progress/done/rejected)은 비활성(드롭다운 미노출)이나
// 표시 폴백을 위해 라벨/색 포함. 기존 데이터는 마이그레이션으로 대기 초기화됨.

export type LeadStatus =
  // 활성
  | "pending"
  | "approved"
  | "prospect"
  | "recall_scheduled"
  | "visit_scheduled"
  | "consult_done"
  | "on_hold"
  | "disconnected"
  | "no_answer"
  | "wrong_number"
  | "not_self"
  | "unqualified"
  | "closed"
  | "canceled"
  // 레거시(비활성)
  | "in_progress"
  | "done"
  | "rejected";

export interface LeadStatusMeta {
  value: LeadStatus;
  label: string;
  color: string; // 상태별 개별 색 (점/차트).
  active: boolean; // false = 드롭다운 미노출(레거시).
}

// 표시·드롭다운 순서 = 이 배열 순서.
export const LEAD_STATUS_META: LeadStatusMeta[] = [
  { value: "pending", label: "대기", color: "#6b7280", active: true },
  { value: "approved", label: "승인", color: "#16a34a", active: true },
  { value: "prospect", label: "가망", color: "#2563eb", active: true },
  { value: "recall_scheduled", label: "재통화예정", color: "#4f46e5", active: true },
  { value: "visit_scheduled", label: "방문예정", color: "#0891b2", active: true },
  { value: "consult_done", label: "상담완료", color: "#0d9488", active: true },
  { value: "on_hold", label: "보류", color: "#d97706", active: true },
  { value: "disconnected", label: "단선", color: "#ea580c", active: true },
  { value: "no_answer", label: "부재", color: "#ca8a04", active: true },
  { value: "wrong_number", label: "전화번호 오류", color: "#b45309", active: true },
  { value: "not_self", label: "본인아님", color: "#9333ea", active: true },
  { value: "unqualified", label: "기준미달", color: "#e11d48", active: true },
  { value: "closed", label: "종결", color: "#475569", active: true },
  { value: "canceled", label: "취소", color: "#dc2626", active: true },
  // 레거시(비활성) — 표시 폴백.
  { value: "in_progress", label: "진행", color: "#9ca3af", active: false },
  { value: "done", label: "완료", color: "#9ca3af", active: false },
  { value: "rejected", label: "거절", color: "#9ca3af", active: false },
];

// 드롭다운 노출용 활성 목록.
export const ACTIVE_LEAD_STATUSES = LEAD_STATUS_META.filter((m) => m.active);

const BY_VALUE = new Map<LeadStatus, LeadStatusMeta>(
  LEAD_STATUS_META.map((m) => [m.value, m]),
);

export function statusMeta(value: LeadStatus): LeadStatusMeta {
  return (
    BY_VALUE.get(value) ?? {
      value,
      label: value,
      color: "#9ca3af",
      active: false,
    }
  );
}
export function statusLabel(value: LeadStatus): string {
  return statusMeta(value).label;
}
export function statusColor(value: LeadStatus): string {
  return statusMeta(value).color;
}

// 집계/필터에서 쓰는 상태 값 — 문자열 리터럴 산재 방지.
export const APPROVED_STATUS: LeadStatus = "approved";
export const CANCELED_STATUS: LeadStatus = "canceled";
export const NO_ANSWER_STATUS: LeadStatus = "no_answer";

// 부재 탭 = 부재/단선 & 연락시도 누적이 상한 미만. FE 안내문·BE 필터 공통 기준.
export const RETRY_STATUSES = [
  "no_answer",
  "disconnected",
] as const satisfies readonly LeadStatus[];
export const ABSENT_ATTEMPT_CAP = 7;
